import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { ConfigurationCustomerPatchInBatchRequest, ConfigurationCustomerResponse } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.dto'
import { ConfigurationLocalAccountingCustomersModalContactInformationState, initialConfigurationLocalAccountingCustomersModalContactInformationState } from '@/modules/configurationModule/detail/local-accounting/billable/clients/components/clients-modal-contact-information/configuration-local-accounting-billable-clients-modal-contact-information.model'
import { commonAddressService } from '@/api/resources/common/address-services/address-services'
import { CommonCountryResponse, CommonMunicipalityResponse, CommonProvinceResponse } from '@/api/services/common.dto'
import { configurationLocalAccountingBillableCustomerService } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-services/configuration-local-accounting-billable-clients-customer-services.service'
import {
    PredefinedCustomNotificationResponse
} from '@/modules/configurationModule/detail/notifications/services/predefined-custom-notification/predefined-custom-notification.dto'
import {
    predefinedCustomNotificationConfigurationService
} from '@/modules/configurationModule/detail/notifications/services/predefined-custom-notification/predefined-custom-notification.service'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import {
    NotificationTemplateType
} from '@/modules/configurationModule/detail/notifications/models/notification-templates'
import { reportTemplateService } from '@/api/services/configuration/report-templates/report-templates.service'
import { ReportTemplateResponse, ReportTemplateType } from '@/api/services/configuration/report-templates/report-templates.dto'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { GridUtils } from '@/shared/util/grid-utils'
@Component({
    components: {
    }
})
export default class ConfigurationLocalAccountingBillableClientsModalContactInformation extends Vue {
    @Prop() readonly customer!: ConfigurationCustomerResponse

    @Emit()
    save(): void {}

    state: ConfigurationLocalAccountingCustomersModalContactInformationState
    constructor() {
        super()
        this.state = initialConfigurationLocalAccountingCustomersModalContactInformationState()
    }

    get isCountrySelected(): boolean {
        return !!this.state.country
    }

    get isProvinceSelected(): boolean {
        return !!this.state.province
    }

    get modelValidated(): boolean {
        const hasErrors: boolean = this.$validator.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    get invoiceReportType(): ReportTemplateType {
        return ReportTemplateType.INVOICE_REPORT
    }

    get deliveryNoteReportType(): ReportTemplateType {
        return ReportTemplateType.BILLABLE_DELIVERY_NOTE_REPORT
    }

    async mounted(): Promise<void> {
        await this.loadCountries()
        if (this.customer) {
            this.state = {
                ...this.state,
                description: this.customer.description,
                address: this.customer.address.address,
                postalCode: this.customer.address.postalCode,
                contactName: this.customer.contactName,
                emailNotification: this.customer.contactInformation.email,
                email: this.customer.email,
                phoneNotification: this.customer.contactInformation.mainPhone,
                phone1: this.customer.contactInformation.otherPhone1,
                phone2: this.customer.contactInformation.otherPhone2,
                smsNotificationEnabled: this.customer.contactInformation.smsNotificationEnabled,
                emailNotificationEnabled: this.customer.contactInformation.emailNotificationEnabled,
                breakdownDeliveryNotesByCaseType: this.customer.breakdownDeliveryNotesByCaseType,
                breakdownDeliveryNotesByPatient: this.customer.breakdownDeliveryNotesByPatient,
                breakdownDeliveryNotesByRegime: this.customer.breakdownDeliveryNotesByRegime,
                predefinedCustomNotificationForAuthorization: this.customer.predefinedCustomNotificationForAuthorization,
                reportDeliveryNote: this.customer.billableDeliveryNoteReportTemplate,
                reportInvoice: this.customer.invoiceReportTemplate
            }

            await this._loadProvincesAndMunicipalitiesWhenCustomerExists(this.customer)
        }
    }

    async loadCountries(): Promise<void> {
        const countries = await PromiseUtils.toCallSecure(commonAddressService.getCountries())
        this.state.countries = countries ?? []
    }

    async onSelectCountry(country: CommonCountryResponse): Promise<void> {
        const provinces = await PromiseUtils.toCallSecure(commonAddressService.getProvinces(country.id))
        this.state = {
            ...this.state,
            provinces: provinces ?? [],
            province: null,
            municipality: null
        }
    }

    async onSelectProvince(province: CommonProvinceResponse): Promise<void> {
        const municipalities = await PromiseUtils.toCallSecure(commonAddressService.getMunicipalities(province.id))
        this.state = {
            ...this.state,
            municipalities: municipalities ?? [],
            municipality: null
        }
    }

    async loadReportTemplates(type: ReportTemplateType): Promise<ReportTemplateResponse[]> {
        const filterModel: CommonFilterModel = {
            type: GridUtils.getSetFilterModel([type])
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const reportTemplates = await PromiseUtils.toCallSecure(reportTemplateService.getList(filterModel, params))
        return reportTemplates?.content ?? []
    }

    onClearProvince(): void {
        this.state = {
            ...this.state,
            province: null,
            provinces: []
        }
        this.onClearMunicipality()
    }

    onClearMunicipality(): void {
        this.state = {
            ...this.state,
            municipality: null,
            municipalities: []
        }
    }

    async doAction(): Promise<void> {
        const request: ConfigurationCustomerPatchInBatchRequest = {
            id: this.customer.id,
            description: this.state.description,
            address: {
                address: this.state.address,
                postalCode: this.state.postalCode,
                countryId: this.state.country?.id,
                provinceId: this.state.province?.id,
                municipalityId: this.state.municipality?.id
            },
            contactName: this.state.contactName,
            email: this.state.email,
            contactInformation: {
                email: this.state.emailNotification,
                emailNotificationEnabled: this.state.emailNotificationEnabled,
                mainPhone: this.state.phoneNotification,
                otherPhone1: this.state.phone1,
                otherPhone2: this.state.phone2,
                smsNotificationEnabled: this.state.smsNotificationEnabled
            },
            breakdownDeliveryNotesByCaseType: this.state.breakdownDeliveryNotesByCaseType,
            breakdownDeliveryNotesByPatient: this.state.breakdownDeliveryNotesByPatient,
            breakdownDeliveryNotesByRegime: this.state.breakdownDeliveryNotesByRegime,
            predefinedCustomNotificationForAuthorizationId: this.state.predefinedCustomNotificationForAuthorization?.id ?? null,
            invoiceReportTemplateId: this.state.reportInvoice?.id ?? null,
            billableDeliveryNoteReportTemplateId: this.state.reportDeliveryNote?.id ?? null
        }
        const response = await PromiseUtils.toCallSecure(configurationLocalAccountingBillableCustomerService.patch(
                this.$store.getters.labId!,
                [request]
        ))
        if (response) {
            this.save()
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
        }
        await this.$validator.reset()
    }

    private async _loadProvincesAndMunicipalitiesWhenCustomerExists(customer: ConfigurationCustomerResponse): Promise<void> {
        if (customer.address.country) {
            this.state.country = this.state.countries.find((country: CommonCountryResponse) => country.id === customer.address.country.id)!
            this.state.provinces = await PromiseUtils.toCallSecure(commonAddressService.getProvinces(customer.address.country.id)) || []
            if (customer.address.province) {
                this.state.province = this.state.provinces.find((province: CommonProvinceResponse) => province.id === customer.address.province.id)!
                this.state.municipalities = await PromiseUtils.toCallSecure(commonAddressService.getMunicipalities(customer.address.province.id)) || []
                this.state.municipality = this.state.municipalities.find((municipality: CommonMunicipalityResponse) => municipality.id === customer.address.municipality.id)!
            }
        }
    }

    private async _getPredefinedCustomNotificationForAuthorization(): Promise<PredefinedCustomNotificationResponse[]> {
        const filterModel: CommonFilterModel = {
            type: {
                filterType: 'set',
                values: [NotificationTemplateType.APPLIED_BILLABLE_CONCEPT_AUTHORIZATION]
            }
        }
        const response =
                await PromiseUtils.toCallSecure(
                    predefinedCustomNotificationConfigurationService.paginator(
                        filterModel,
                        undefined,
                        this.$store.getters.labId))
        return response?.content ?? []
    }
}
